import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import {
  whatsappServersService,
  type CreateServerData,
  type UpdateServerData,
} from '../api/whatsapp-servers.service';

function invalidateWhatsapp(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.whatsappServers.all });
}

export function useWhatsAppServerMutations() {
  const qc = useQueryClient();
  const onError = useErrorToast();

  const createServer = useMutation({
    mutationFn: (data: CreateServerData) => whatsappServersService.create(data),
    onSuccess: () => {
      invalidateWhatsapp(qc);
      toast.success('Servidor criado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao criar servidor'),
  });

  const updateServer = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServerData }) =>
      whatsappServersService.update(id, data),
    onSuccess: () => {
      invalidateWhatsapp(qc);
      toast.success('Servidor atualizado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao atualizar servidor'),
  });

  const deleteServer = useMutation({
    mutationFn: (id: string) => whatsappServersService.delete(id),
    onSuccess: () => {
      invalidateWhatsapp(qc);
      toast.success('Servidor deletado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao deletar servidor'),
  });

  const healthCheckServer = useMutation({
    mutationFn: (id: string) => whatsappServersService.healthCheck(id),
    onSuccess: () => {
      invalidateWhatsapp(qc);
      toast.success('Health check executado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao executar health check'),
  });

  const healthCheckAllServers = useMutation({
    mutationFn: () => whatsappServersService.healthCheckAll(),
    onSuccess: (result) => {
      invalidateWhatsapp(qc);
      toast.success(`Health check concluído: ${result.healthy} saudáveis, ${result.unhealthy} não saudáveis`);
    },
    onError: (e) => onError(e, 'Erro ao executar health check'),
  });

  return { createServer, updateServer, deleteServer, healthCheckServer, healthCheckAllServers };
}
